import { useEffect, useState } from "react";
import { BadgeCheck, MapPin, Globe, Loader2, Sparkles, Building2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { orgLabel } from "@/lib/constants";

// Shared lead/opportunity/business detail modal.
// `userId` is the profile's user_id; `match` optionally carries AI-match context.
export default function LeadDetailModal({ open, onClose, userId, match }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !userId) return;
    setLoading(true);
    setError("");
    setProfile(null);
    api.get(`/profiles/${userId}`)
      .then(({ data }) => setProfile(data))
      .catch((err) => setError(formatApiError(err.response?.data?.detail) || "Could not load details"))
      .finally(() => setLoading(false));
  }, [open, userId]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="lead-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 size={18} className="text-[#2563EB]" />
            {match?.org_name || profile?.org_name || "Details"}
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="py-10 flex justify-center" data-testid="lead-detail-loading">
            <Loader2 className="animate-spin text-slate-400" size={26} />
          </div>
        )}

        {error && !loading && (
          <p className="py-6 text-sm text-red-600" data-testid="lead-detail-error">{error}</p>
        )}

        {profile && !loading && (
          <div className="space-y-4" data-testid="lead-detail-content">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <span className="capitalize font-medium text-[#0A0F1C]">{orgLabel(profile.org_type)}</span>
              {profile.verified && <span className="flex items-center gap-1 text-[#2563EB]"><BadgeCheck size={15} /> Verified</span>}
              {typeof profile.trust_score === "number" && (
                <span className="font-mono text-xs text-slate-400">· Trust {profile.trust_score}</span>
              )}
            </div>

            {match && (
              <div className="rounded-lg bg-[#2563EB]/5 border border-[#2563EB]/20 p-3" data-testid="lead-detail-match">
                <div className="flex items-center gap-2 text-xs font-semibold text-[#2563EB]">
                  <Sparkles size={13} /> {match.opportunity} · Match score {match.score}
                </div>
                {match.reason && <p className="mt-1 text-sm text-slate-600">{match.reason}</p>}
              </div>
            )}

            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
              {profile.industry && <span>{profile.industry}</span>}
              {profile.location && <span className="flex items-center gap-1"><MapPin size={11} /> {profile.location}</span>}
              {profile.website && (
                <a href={profile.website} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1 text-[#2563EB] hover:underline" data-testid="lead-detail-website">
                  <Globe size={11} /> Website
                </a>
              )}
            </div>

            {profile.description && (
              <p className="text-sm text-slate-600 leading-relaxed">{profile.description}</p>
            )}

            {profile.looking_for && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Looking for</p>
                <p className="text-sm text-slate-600">{profile.looking_for}</p>
              </div>
            )}
            {profile.offerings && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Offerings</p>
                <p className="text-sm text-slate-600">{profile.offerings}</p>
              </div>
            )}
            {profile.certifications?.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {profile.certifications.map((c, i) => (
                  <span key={i} className="text-[11px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{c}</span>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
